// src/components/ClassStudentsModal.js

import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  DeleteOutline as DeleteOutlineIcon,
  Edit as EditIcon,
  SwapHoriz as SwapIcon,
  Refresh as RefreshIcon,
  PersonAddAlt1 as PersonAddIcon,
} from '@mui/icons-material';
import StudentForm from './StudentForm';
import StudentEditForm from './StudentEditForm';
const YEAR_OPTIONS = [
  { value: 'primo',   label: 'Primo anno' },
  { value: 'secondo', label: 'Secondo anno' },
  { value: 'terzo',   label: 'Terzo anno' },
  { value: 'quarto',  label: 'Quarto anno' },
  { value: 'quinto',  label: 'Quinto anno' },
  { value: 'sesto',   label: 'Sesto anno' },
  { value: 'settimo', label: 'Settimo anno' },
];


function ClassStudentsModal({ open, onClose }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sourceClass, setSourceClass] = useState('');
  const [targetClass, setTargetClass] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [editStudent, setEditStudent] = useState(null);

  useEffect(() => {
    if (open) {
      loadStudents();
      resetSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const resetSelection = () => {
    setSelectedIds([]);
    setSourceClass('');
    setTargetClass('');
    setSearchTerm('');
    setAddOpen(false);
    setEditStudent(null);
  };

  const loadStudents = async () => {
    try {
      setLoading(true);
      const snap = await getDocs(collection(db, 'students'));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setStudents(list);
    } catch (e) {
      console.error('Errore caricamento studenti per Classi & Studenti:', e);
    } finally {
      setLoading(false);
    }
  };


  const filteredStudents = useMemo(
    () =>
      students
        .filter((s) => {
          if (sourceClass) {
            const cls = (s.classe || '').trim();
            if (cls !== sourceClass) return false;
          }
          if (searchTerm.trim()) {
            const q = searchTerm.trim().toLowerCase();
            const nome = (s.nome || '').toLowerCase();
            const cognome = (s.cognome || '').toLowerCase();
            return nome.includes(q) || cognome.includes(q);
          }
          return true;
        })
        .sort((a, b) => {
          const aLast = (a.cognome || '').toLowerCase();
          const bLast = (b.cognome || '').toLowerCase();
          if (aLast < bLast) return -1;
          if (aLast > bLast) return 1;
          const aFirst = (a.nome || '').toLowerCase();
          const bFirst = (b.nome || '').toLowerCase();
          if (aFirst < bFirst) return -1;
          if (aFirst > bFirst) return 1;
          return 0;
        }),
    [students, sourceClass, searchTerm]
  );

  const allVisibleSelected =
    filteredStudents.length > 0 && filteredStudents.every((s) => selectedIds.includes(s.id));
  const someVisibleSelected = filteredStudents.some((s) => selectedIds.includes(s.id));

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) =>
        prev.filter((id) => !filteredStudents.some((s) => s.id === id))
      );
    } else {
      const idsToAdd = filteredStudents.map((s) => s.id);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...idsToAdd])));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleMoveSelected = async () => {
    if (!targetClass.trim()) {
      alert('Seleziona una classe di destinazione.');
      return;
    }
    if (selectedIds.length === 0) {
      alert('Seleziona almeno uno studente da spostare.');
      return;
    }
    try {
      setBusy(true);
      const dest = targetClass.trim();
      const ops = selectedIds.map(async (id) => {
        const ref = doc(db, 'students', id);
        await updateDoc(ref, { classe: dest });
      });
      await Promise.all(ops);
      await loadStudents();
      setSelectedIds([]);
    } catch (e) {
      console.error('Errore spostamento studenti:', e);
      alert("Si è verificato un errore durante lo spostamento degli studenti.");
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) {
      alert('Seleziona almeno uno studente da eliminare.');
      return;
    }
    if (!window.confirm('Sei sicuro di voler eliminare gli studenti selezionati?')) {
      return;
    }
    try {
      setBusy(true);
      const ops = selectedIds.map(async (id) => {
        const ref = doc(db, 'students', id);
        await deleteDoc(ref);
      });
      await Promise.all(ops);
      await loadStudents();
      setSelectedIds([]);
    } catch (e) {
      console.error('Errore eliminazione studenti:', e);
      alert("Si è verificato un errore durante l'eliminazione degli studenti.");
    } finally {
      setBusy(false);
    }
  };

  const handleAddStudent = async (addedStudent) => {
    try {
      if ((targetClass || sourceClass) && addedStudent?.id) {
        const ref = doc(db, 'students', addedStudent.id);
        const cls = (targetClass || sourceClass || '').trim();
        if (cls) {
          await updateDoc(ref, { classe: cls });
        }
      }
    } catch (e) {
      console.error('Errore assegnazione classe al nuovo studente:', e);
    } finally {
      setAddOpen(false);
      await loadStudents();
    }
  };

  const handleEditStudentSaved = async () => {
    setEditStudent(null);
    await loadStudents();
  };

  const handleClose = () => {
    if (busy) return;
    if (onClose) onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
        <DialogTitle>Classi &amp; Studenti</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Gestisci le classi assegnando o spostando gli studenti da una classe all&apos;altra.
              Puoi filtrare per classe di origine, selezionare più studenti e aggiornarne la classe,
              utilizzare le modali complete di aggiunta/modifica studente, oppure eliminare definitivamente
              le schede studente.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel id="source-class-label">Classe di origine</InputLabel>
              <Select
                labelId="source-class-label"
                value={sourceClass}
                label="Classe di origine"
                onChange={(e) => setSourceClass(e.target.value)}
              >
                <MenuItem value="">
                  <em>Tutte</em>
                </MenuItem>
                {YEAR_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel id="target-class-label">Classe di destinazione</InputLabel>
              <Select
                labelId="target-class-label"
                value={targetClass}
                label="Classe di destinazione"
                onChange={(e) => setTargetClass(e.target.value)}
              >
                <MenuItem value="">
                  <em>Seleziona…</em>
                </MenuItem>
                {YEAR_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              label="Cerca per nome o cognome"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flexGrow: 1, minWidth: 220 }}
            />

            <Tooltip title="Ricarica elenco">
              <span>
                <IconButton onClick={loadStudents} disabled={loading || busy} size="small">
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <Box
            sx={{
              mb: 1,
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="body2">
              Studenti visibili: {filteredStudents.length} / {students.length}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={() => setAddOpen(true)}
                disabled={busy}
              >
                Aggiungi studente
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<SwapIcon />}
                onClick={handleMoveSelected}
                disabled={busy || selectedIds.length === 0}
              >
                Sposta selezionati
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={handleDeleteSelected}
                disabled={busy || selectedIds.length === 0}
              >
                Elimina selezionati
              </Button>
            </Box>
          </Box>

          <Box sx={{ maxHeight: '50vh', overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={someVisibleSelected && !allVisibleSelected}
                      checked={allVisibleSelected && filteredStudents.length > 0}
                      onChange={toggleSelectAllVisible}
                    />
                  </TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>Cognome</TableCell>
                  <TableCell>Classe</TableCell>
                  <TableCell align="right">Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2">Caricamento studenti...</Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredStudents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2">
                        Nessuno studente trovato per i filtri applicati.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredStudents.map((s) => {
                    const isSelected = selectedIds.includes(s.id);
                    return (
                      <TableRow key={s.id} hover selected={isSelected}>
                        <TableCell padding="checkbox">
                          <Checkbox checked={isSelected} onChange={() => toggleSelectOne(s.id)} />
                        </TableCell>
                        <TableCell>{s.nome || ''}</TableCell>
                        <TableCell>{s.cognome || ''}</TableCell>
                        <TableCell>
                          {(() => {
                            const raw = (s.classe || '').trim();
                            if (!raw) return <em>Senza classe</em>;
                            const opt = YEAR_OPTIONS.find((o) => o.value === raw);
                            return opt ? opt.label : raw;
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Modifica studente (modale completa)">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => setEditStudent(s)}
                                disabled={busy}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={busy}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>

      {addOpen && (
        <StudentForm
          open={addOpen}
          onClose={() => {
            setAddOpen(false);
            loadStudents();
          }}
          onAddStudent={handleAddStudent}
        />
      )}

      {editStudent && (
        <StudentEditForm
          open={!!editStudent}
          student={editStudent}
          onClose={handleEditStudentSaved}
        />
      )}
    </>
  );
}

export default ClassStudentsModal;
